/**
 * AtlasTownFixturesModule — the TOWN LIFE wave's fixtures in the world (2026-09-24).
 *
 *   TERMINALS  the bounty guild's terminal and the contract board stand on their activity-station rows (the server
 *              judges presence against the same rows): the generated models (town_props_loader) — a procedural
 *              kiosk / notice board until they land — a box collider within reach, and E opens the window
 *              (`atlas:open-bounty-board` / `atlas:open-contract-board`).
 *   FIRES      every town's fire circle (the `fire_ring` station prop: stones, a laid fire and a LOG SEAT under
 *              every slot, so a versona sitting fireside sits on a log) and every PLAYER CAMP (the same ring at a
 *              camp row) burn — through the FIRE SYSTEM (fire realism, 2026-09-25: lib/vfx/fire_system.ts): the
 *              flame on its quality rung, the glowing coal bed, embers, smoke, a char mark, the crackle and pops,
 *              and a light from the shared broker (THE LIGHT-SET IDENTITY LAW lives there now). The old sprites,
 *              the private pool of two lights and the sine flicker are gone. A camp carries its name over it.
 *   SITTING    E at a fire walks you onto the nearest free log and sits you facing the flames (`atlas.seated`, the
 *              furniture module's pose — any move key or E stands you up) and asks the server to WARM you
 *              (`warmByFire`: Social +8, Rest +3, once per ten minutes).
 * ONE KEY, ONE FOCUS: every candidate claims the arbiter each frame (terminals priority 3, fires 1).
 * Levers: `__townFixtures()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { addFire, removeFire, fireSystem } from '../lib/vfx/fire_system.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { groundYIn } from '../lib/grounding.js';
import { interactFocus } from '../lib/interact_focus.js';
import { onInteract, interactPrompt } from '../lib/interact_input.js';
import { buildStationProp, SEAT_H } from '../lib/station_props.js';
import { stationDef, formationSlot } from '../lib/activity_catalog.js';
import { seatPose } from '../lib/furniture_functions.js';
import { prefetchTownProps, townProp, townPropSize, townPropStats } from './town/town_props_loader.js';
import type { PhysicsModule } from './PhysicsModule.js';
import type { AtlasInputModule } from './AtlasInputModule.js';

const TERMINAL_KINDS = ['bounty_terminal', 'contract_board', 'bank_terminal'] as const;
const TERMINAL_LABEL: Record<string, string> = { bounty_terminal: 'Bounty terminal', contract_board: 'Contract board', bank_terminal: 'Bank terminal' };
const TERMINAL_DRAW_M = 320;
const TERMINAL_COLLIDE_M = 60;
const TERMINAL_USE_M = 4.5;
const FIRE_DRAW_M = 180;
const FIRE_USE_M = 4.2;
/** The flame's base over the ring's ground: the top of the laid logs. */
const FIRE_BASE_Y = 0.12;

interface StationLike { stationId: bigint; worldId: bigint; settlementId: bigint; kind: string; x: number; z: number; facing: number }
interface CampLike { campId: bigint; owner: { isEqual(o: unknown): boolean; toHexString(): string }; worldId: bigint; x: number; z: number; yaw: number; name: string }

interface Terminal { key: string; kind: string; stationId: string; sid: bigint; x: number; y: number; z: number; facing: number; group: THREE.Group; generated: boolean; collider: boolean }
interface Fire {
  key: string; x: number; y: number; z: number; facing: number; camp: boolean; name: string;
  group: THREE.Group;
  seats: Array<{ x: number; z: number; yaw: number }>;
  /** Registered with the fire system (it may come up a frame after the first scan). */
  lit: boolean;
}

/** A name plate over a camp (a canvas sprite, drawn once). */
function namePlate(text: string): THREE.Sprite {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 96;
  const g = c.getContext('2d')!;
  g.fillStyle = 'rgba(12,10,8,0.62)';
  g.fillRect(0, 12, 512, 72);
  g.font = '600 40px Sora, sans-serif';
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillStyle = '#ffd9a3';
  g.fillText(text.slice(0, 28), 256, 48);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  const m = new THREE.SpriteMaterial({ map: t, transparent: true, depthWrite: false });
  markMrtOverlay(m);
  const s = new THREE.Sprite(m);
  s.scale.set(3.2, 0.6, 1);
  s.position.set(0, 2.6, 0);
  s.userData.ownTexture = true;
  return s;
}

/** A procedural stand-in until the generated model lands: a kiosk with a red screen, or a two-post notice board. */
function standIn(kind: string): THREE.Group {
  const g = new THREE.Group();
  const alloy = new THREE.MeshStandardMaterial({ color: 0x9aa3ad, roughness: 0.6, metalness: 0.4 });
  if (kind === 'bounty_terminal' || kind === 'bank_terminal') {
    const body = new THREE.Mesh(new THREE.BoxGeometry(kind === 'bank_terminal' ? 0.76 : 0.6, 1.9, 0.5), alloy);
    body.position.y = 0.95;
    const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.44, 0.55), new THREE.MeshBasicMaterial({ color: kind === 'bank_terminal' ? 0x2fb8b0 : 0xb33020 }));
    screen.position.set(0, 1.45, 0.26);
    g.add(body, screen);
  } else {
    const wood = new THREE.MeshStandardMaterial({ color: 0x7a5a3a, roughness: 0.9 });
    for (const x of [-0.65, 0.65]) { const p = new THREE.Mesh(new THREE.BoxGeometry(0.1, 2.2, 0.1), wood); p.position.set(x, 1.1, 0); g.add(p); }
    const panel = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.0, 0.06), wood);
    panel.position.set(0, 1.55, 0);
    const paper = new THREE.Mesh(new THREE.PlaneGeometry(1.3, 0.8), new THREE.MeshStandardMaterial({ color: 0xe8e2d2, roughness: 1 }));
    paper.position.set(0, 1.55, 0.035);
    g.add(panel, paper);
  }
  g.traverse((o) => { if ((o as THREE.Mesh).isMesh) { o.castShadow = true; o.receiveShadow = true; } });
  g.userData.ownGeometry = true;
  return g;
}

function disposeOwned(root: THREE.Object3D): void {
  root.traverse((o) => {
    if (o.userData.sharedAsset) return;
    const m = o as THREE.Mesh;
    if (root.userData.ownGeometry && m.isMesh) { m.geometry?.dispose(); (m.material as THREE.Material)?.dispose?.(); }
    if (o.userData.ownTexture) { const sm = (o as THREE.Sprite).material as THREE.SpriteMaterial; sm.map?.dispose(); sm.dispose(); }
  });
}

export class AtlasTownFixturesModule implements IModule {
  readonly name = 'AtlasTownFixtures';
  private app!: App;
  private scene!: THREE.Scene;
  private terminals = new Map<string, Terminal>();
  private fires = new Map<string, Fire>();
  private prompt: HTMLElement | null = null;
  private focused: { id: string; label: string; act: () => void } | null = null;
  private scanAt = 0;
  private sitWalk: { fire: Fire; seat: { x: number; z: number; yaw: number }; until: number } | null = null;
  /** F2: the bound Interact key AND the pad's (`atlas:place-requested`), one press — lib/interact_input.ts. */
  private offInteract: (() => void) | null = null;
  private subscribed = false;

  constructor(private readonly physics: PhysicsModule) {}

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    const p = document.createElement('div');
    p.id = 'town-fixture-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-accent) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(p);
    this.prompt = p;
    // The fires' lights are the shared broker's (AtlasFireModule): nothing here adds a light any more.
    this.offInteract = onInteract(() => this.key());
    app.world.addSystem(makeSystem('TownFixtures', Phase.UI, 38, () => this.tick()));
    app.world.addSystem(makeSystem('TownFixturesSitWalk', Phase.FixedSim, 39, () => this.stepSitWalk()));
    bus.on('atlas:account-ready', () => { this.subscribe(); void prefetchTownProps(app); });
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => this.clearAll());
    (window as unknown as { __townFixtures?: unknown }).__townFixtures = () => this.diag();
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subscribed) return;
    this.subscribed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM player_camp']); } catch { /* reconnect handles it */ }
  }

  // ── The frame ───────────────────────────────────────────────────────────────
  private tick(): void {
    const now = performance.now();
    const inWorld = atlas.currentWorldId !== null && !atlas.spaceFlight && !!atlas.conn;
    if (!inWorld) { if (this.terminals.size || this.fires.size) this.clearAll(); this.showPrompt(null); return; }
    if (now - this.scanAt > 1000) { this.scanAt = now; this.scan(); }
    this.lightFires();
    this.claimAndPrompt();
  }

  private scan(): void {
    const conn = atlas.conn!;
    const wid = atlas.currentWorldId!;
    const px = atlas.localX, pz = atlas.localZ;
    const db = conn.db as unknown as { activityStation?: { iter(): Iterable<StationLike> }; playerCamp?: { iter(): Iterable<CampLike> } };
    const keepT = new Set<string>(), keepF = new Set<string>();
    for (const r of db.activityStation?.iter() ?? []) {
      if (r.worldId !== wid) continue;
      const d = Math.hypot(r.x - px, r.z - pz);
      if ((TERMINAL_KINDS as readonly string[]).includes(r.kind)) {
        if (d > TERMINAL_DRAW_M) continue;
        const key = `st:${r.stationId}`;
        keepT.add(key);
        this.ensureTerminal(key, r);
      } else if (r.kind === 'fire_circle') {
        if (d > FIRE_DRAW_M) continue;
        const key = `st:${r.stationId}`;
        keepF.add(key);
        this.ensureFire(key, r.x, r.z, r.facing, false, '');
      }
    }
    for (const c of db.playerCamp?.iter() ?? []) {
      if (c.worldId !== wid) continue;
      if (Math.hypot(c.x - px, c.z - pz) > FIRE_DRAW_M) continue;
      const key = `camp:${c.campId}`;
      keepF.add(key);
      this.ensureFire(key, c.x, c.z, c.yaw, true, c.name);
    }
    for (const [k, t] of this.terminals) {
      if (keepT.has(k)) {
        // Colliders only near the player; a landed model replaces the stand-in.
        const d = Math.hypot(t.x - px, t.z - pz);
        const want = d < TERMINAL_COLLIDE_M;
        if (want !== t.collider) this.setCollider(t, want);
        if (!t.generated && townProp(t.kind)) this.rebuildTerminal(t);
        continue;
      }
      this.removeTerminal(t);
      this.terminals.delete(k);
    }
    for (const [k, f] of this.fires) {
      if (keepF.has(k)) continue;
      this.scene.remove(f.group);
      disposeOwned(f.group);
      removeFire(`town:${k}`);
      this.fires.delete(k);
    }
  }

  private ground(x: number, z: number): number {
    return atlas.conn ? groundYIn(atlas.conn, atlas.currentWorldId, x, z) : 0;
  }

  // ── Terminals ───────────────────────────────────────────────────────────────
  private ensureTerminal(key: string, r: StationLike): void {
    if (this.terminals.has(key)) return;
    const y = this.ground(r.x, r.z);
    const t: Terminal = { key, kind: r.kind, stationId: r.stationId.toString(), sid: r.settlementId, x: r.x, y, z: r.z, facing: r.facing, group: new THREE.Group(), generated: false, collider: false };
    this.buildTerminalModel(t);
    this.terminals.set(key, t);
  }
  private buildTerminalModel(t: Terminal): void {
    const model = townProp(t.kind);
    t.generated = !!model;
    const g = model ?? standIn(t.kind);
    g.position.set(t.x, t.y, t.z);
    g.rotation.y = t.facing;
    g.name = `town-fixture:${t.kind}`;
    t.group = g;
    this.scene.add(g);
  }
  private rebuildTerminal(t: Terminal): void {
    this.scene.remove(t.group);
    disposeOwned(t.group);
    this.buildTerminalModel(t);
  }
  private setCollider(t: Terminal, on: boolean): void {
    const id = `townfix:${t.key}`;
    t.collider = on;
    if (!on) { this.physics.removeStaticCollider(id); return; }
    const size = townPropSize(t.kind) ?? new THREE.Vector3(t.kind === 'bounty_terminal' ? 0.6 : t.kind === 'bank_terminal' ? 0.76 : 1.5, 2.2, 0.5);
    this.physics.addStaticCollider(id, { shape: 'box', halfExtents: [Math.max(0.25, size.x / 2), size.y / 2, Math.max(0.2, size.z / 2)] }, { x: t.x, y: t.y + size.y / 2, z: t.z, yaw: t.facing, scale: 1 });
  }
  private removeTerminal(t: Terminal): void {
    if (t.collider) this.physics.removeStaticCollider(`townfix:${t.key}`);
    this.scene.remove(t.group);
    disposeOwned(t.group);
  }

  // ── Fires ───────────────────────────────────────────────────────────────────
  private ensureFire(key: string, x: number, z: number, facing: number, camp: boolean, name: string): void {
    if (this.fires.has(key)) return;
    const y = this.ground(x, z);
    const group = new THREE.Group();
    group.name = `town-fire:${key}`;
    group.position.set(x, y, z);
    group.rotation.y = facing;
    const prop = buildStationProp('fire_circle');
    if (prop) { prop.matrixAutoUpdate = true; group.add(prop); }
    if (camp && name) group.add(namePlate(name));
    // The log seats in WORLD coordinates (the same slots the prop cut them from), each facing the fire.
    const def = stationDef('fire_circle');
    const seats: Fire['seats'] = [];
    if (def) {
      const cos = Math.cos(facing), sin = Math.sin(facing);
      for (let i = 0; i < def.capacity; i++) {
        const s = formationSlot(def.formation, i, def.capacity, def.spacing);
        // three's rotation about Y: local (lx, lz) → (lx cos + lz sin, −lx sin + lz cos)
        const wx = x + s.x * cos + s.z * sin, wz = z - s.x * sin + s.z * cos;
        // Facing the fire: forward = (−sin yaw, −cos yaw) points from the seat to the centre.
        const yaw = Math.atan2(wx - x, wz - z);
        seats.push({ x: wx, z: wz, yaw });
      }
    }
    this.scene.add(group);
    this.fires.set(key, { key, x, y, z, facing, camp, name, group, seats, lit: false });
  }

  /** Hand every fire to the fire system (once each; the system owns the flame, the coals, the light and the sound). */
  private lightFires(): void {
    for (const f of this.fires.values()) {
      if (f.lit) continue;
      f.lit = addFire(`town:${f.key}`, { x: f.x, y: f.y + FIRE_BASE_Y, z: f.z, kind: 'campfire', bed: true, groundY: f.y });
    }
  }

  // ── E ───────────────────────────────────────────────────────────────────────
  private claimAndPrompt(): void {
    const focus = interactFocus();
    const px = atlas.localX, pz = atlas.localZ;
    let best: { id: string; dist: number; label: string; act: () => void } | null = null;
    if (!atlas.seated) {
      for (const t of this.terminals.values()) {
        const d = Math.hypot(t.x - px, t.z - pz);
        if (d > TERMINAL_USE_M) continue;
        const id = `town:${t.key}`;
        const label = interactPrompt(TERMINAL_LABEL[t.kind] ?? 'Terminal');
        focus.claim({ id, dist: d, priority: 3, label });
        if (!best || d < best.dist) best = { id, dist: d, label, act: () => this.openTerminal(t) };
      }
      for (const f of this.fires.values()) {
        const d = Math.hypot(f.x - px, f.z - pz);
        if (d > FIRE_USE_M) continue;
        const id = `town:fire:${f.key}`;
        const label = interactPrompt(f.camp ? `sit by ${f.name}` : 'sit by the fire');
        focus.claim({ id, dist: d, priority: 1, label });
        if (!best || d < best.dist) best = { id, dist: d, label, act: () => this.sitByFire(f) };
      }
    }
    // Only the focused claim shows its prompt and takes the key.
    const owned = best && focus.owns(best.id) ? best : null;
    this.focused = owned;
    this.showPrompt(owned ? owned.label : null);
  }

  private showPrompt(text: string | null): void {
    if (!this.prompt) return;
    const show = text !== null;
    if ((this.prompt.style.display !== 'none') !== show) this.prompt.style.display = show ? 'block' : 'none';
    if (show && this.prompt.textContent !== text) this.prompt.textContent = text;
  }

  private key(): void {
    if (atlas.seated) return; // the furniture module stands you up
    const f = this.focused;
    if (!f || !interactFocus().owns(f.id)) return;
    f.act();
  }

  private openTerminal(t: Terminal): void {
    const payload = { stationId: t.stationId, settlementId: t.sid.toString() };
    if (t.kind === 'bounty_terminal') bus.emit('atlas:open-bounty-board', payload);
    else if (t.kind === 'bank_terminal') bus.emit('atlas:open-bank', payload);
    else bus.emit('atlas:open-contract-board', payload);
  }

  // ── Sitting by the fire ────────────────────────────────────────────────────
  private sitByFire(f: Fire): void {
    if (f.seats.length === 0) return;
    const px = atlas.localX, pz = atlas.localZ;
    let seat = f.seats[0], bd = Infinity;
    for (const s of f.seats) { const d = Math.hypot(s.x - px, s.z - pz); if (d < bd) { bd = d; seat = s; } }
    if (bd <= 0.7) { this.sitNow(f, seat); return; }
    this.sitWalk = { fire: f, seat, until: performance.now() + 5000 };
  }

  /** FixedSim: walk the last metres onto the log (the input module's own stick — the server row walks with it). */
  private stepSitWalk(): void {
    const w = this.sitWalk;
    if (!w) return;
    const input = this.app.getModule<AtlasInputModule>('AtlasInput');
    const dx = w.seat.x - atlas.localX, dz = w.seat.z - atlas.localZ;
    const d = Math.hypot(dx, dz);
    if (d <= 0.6 || performance.now() > w.until || atlas.seated) {
      input?.setAutoWalk(0, 0, 0);
      this.sitWalk = null;
      if (d <= 1.2 && !atlas.seated) this.sitNow(w.fire, w.seat);
      return;
    }
    // Camera-relative stick: forward = (−sin yaw, −cos yaw), right = (cos yaw, −sin yaw).
    const yaw = atlas.localYaw;
    const fx = -Math.sin(yaw), fz = -Math.cos(yaw), rx = Math.cos(yaw), rz = -Math.sin(yaw);
    input?.setAutoWalk(((dx * fx + dz * fz) / d) * 0.6, ((dx * rx + dz * rz) / d) * 0.6, 0);
  }

  private sitNow(f: Fire, seat: { x: number; z: number; yaw: number }): void {
    const pose = seatPose('sit', 1.0);
    // A log's top is SEAT_H; the pose's eye is measured from the ground the avatar stands on.
    atlas.seated = { x: seat.x, y: f.y + SEAT_H - 0.46, z: seat.z, yaw: seat.yaw + Math.PI, eye: pose.eye, bodyScaleY: pose.bodyScaleY, key: `fire:${f.key}`, fn: 'sit' };
    atlas.localYaw = seat.yaw;
    bus.emit('atlas:reconcile-hold', { ticks: 120, mode: 'hold' });
    bus.emit('atlas:status', { text: 'Sitting by the fire. Any move key stands you up.' });
    const r = atlas.conn?.reducers as unknown as { warmByFire?(a: Record<string, never>): Promise<void> } | undefined;
    r?.warmByFire?.({})
      .then(() => bus.emit('atlas:toast', { text: 'Warmed by the fire — your Social need rises.', kind: 'success', ms: 2600 }))
      .catch(() => { /* warm already, or the server's fire is a step off: a quiet sit */ });
  }

  // ── Teardown + diag ─────────────────────────────────────────────────────────
  private clearAll(): void {
    for (const t of this.terminals.values()) this.removeTerminal(t);
    this.terminals.clear();
    for (const f of this.fires.values()) { this.scene.remove(f.group); disposeOwned(f.group); removeFire(`town:${f.key}`); }
    this.fires.clear();
  }

  private diag(): Record<string, unknown> {
    return {
      terminals: [...this.terminals.values()].map((t) => ({ kind: t.kind, stationId: t.stationId, generated: t.generated, collider: t.collider, dist: Math.round(Math.hypot(t.x - atlas.localX, t.z - atlas.localZ)) })),
      fires: [...this.fires.values()].map((f) => ({ key: f.key, camp: f.camp, name: f.name, seats: f.seats.length, dist: Math.round(Math.hypot(f.x - atlas.localX, f.z - atlas.localZ)) })),
      /** The pool lights the town's fires hold right now (from the shared broker). */
      lights: fireSystem()?.lightIntensities('town:') ?? [],
      focused: this.focused?.label ?? null,
      props: townPropStats(),
      seated: atlas.seated?.key ?? null,
      /** Stand at a fire or terminal (probe lever: the server row moves too). */
      standAt: async (key: string, off = 1.2) => {
        const f = this.fires.get(key), t = this.terminals.get(key);
        const x = (f?.x ?? t?.x ?? 0) + off, z = (f?.z ?? t?.z ?? 0);
        const r = atlas.conn?.reducers as unknown as { debugTeleport?(a: { posX: number; posZ: number }): Promise<void> } | undefined;
        await r?.debugTeleport?.({ posX: x, posZ: z });
        return { x, z };
      },
      use: () => this.focused?.act(),
    };
  }

  dispose(): void {
    this.offInteract?.(); this.offInteract = null;
    this.clearAll();
    this.prompt?.remove();
  }
}
